import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useMemo, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiBarChart2,
  FiCalendar,
  FiChevronRight,
  FiShield,
  FiTag,
} from "react-icons/fi";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  type BuyerPublicBidDetailDto,
  type BuyerPublicBidEspdDto,
  useGetEvaluationQuery,
  useGetPublicBidForBuyerQuery,
  useGetSourcingEventQuery,
} from "../../../../autogen/bff-api";
import { isTwoStageProcedure } from "../../../sourcing-events/buyer/public/utils/procedure";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { useSub } from "../../../../common/subscription/useSub";
import { urls } from "../../../../urls";
import { formatEstimatedValue } from "../../../sourcing-events/portal/utils/displayFormatters";
import { isAwardDecided } from "../bidOutcome";
import { PublicBidStatusTag } from "../PublicBidStatusTag";
import { ClarificationTab } from "./ClarificationTab";
import { DocumentsTab } from "./DocumentsTab";
import { BidDetailSidebar } from "./BidDetailSidebar";
import { DeclarationsTab } from "./DeclarationsTab";
import { EspdTab } from "./EspdTab";
import { HeaderStat } from "./HeaderStat";
import { OverviewTab } from "./OverviewTab";
import { MinimumRequirementsTab } from "./MinimumRequirementsTab";
import { QualificationsTab } from "./QualificationsTab";
import { EvaluationTab } from "./EvaluationTab";
import {
  deriveBidEvaluationSummary,
  formatRankLabel,
} from "./bidEvaluationSummary";

const DASH = "–";

type BidDetailTab =
  | "overview"
  | "documents"
  | "qualifications"
  | "declarations"
  | "minimumRequirements"
  | "espd"
  | "evaluation"
  | "clarification";

type TabBody = FunctionComponent<{ detail: BuyerPublicBidDetailDto }>;

const EvaluationTabAdapter = ({
  detail,
}: {
  detail: BuyerPublicBidDetailDto;
}) => (
  <EvaluationTab
    eventId={detail.bid.forEvent}
    bidId={detail.bid.id}
    bidStatus={detail.bid.status}
  />
);

const QualificationsTabAdapter = ({ detail }: { detail: BuyerPublicBidDetailDto }) => (
  <QualificationsTab eventId={detail.bid.forEvent} bidId={detail.bid.id} detail={detail} />
);

const EspdTabAdapter = ({ detail }: { detail: BuyerPublicBidDetailDto }) => (
  <EspdTab espd={detail.espd} />
);

const DocumentsTabAdapter = ({ detail }: { detail: BuyerPublicBidDetailDto }) => (
  <DocumentsTab documents={detail.bid.documents ?? []} signedDocumentId={detail.bid.signedDocumentId} />
);

const DeclarationsTabAdapter = ({ detail }: { detail: BuyerPublicBidDetailDto }) => (
  <DeclarationsTab declarations={detail.declarations} resetKey={detail.bid.id} />
);

const MinimumRequirementsTabAdapter = ({ detail }: { detail: BuyerPublicBidDetailDto }) => (
  <MinimumRequirementsTab eventId={detail.bid.forEvent} bidId={detail.bid.id} detail={detail} />
);

// Tab labels are rendered via `t(tab.labelKey)` below; list them as literal `t("…")` here
// so i18next-parser keeps the keys in the locale files (it can't see the data-driven call).
// t("Overview") t("Documents") t("Qualification requirements") t("Self-declaration") t("Minimum requirements") t("Exclusion grounds") t("Evaluation") t("Clarification")
const TABS: { key: BidDetailTab; labelKey: string; Body: TabBody }[] = [
  { key: "overview", labelKey: "Overview", Body: OverviewTab },
  { key: "documents", labelKey: "Documents", Body: DocumentsTabAdapter },
  {
    key: "qualifications",
    labelKey: "Qualification requirements",
    Body: QualificationsTabAdapter,
  },
  { key: "declarations", labelKey: "Self-declaration", Body: DeclarationsTabAdapter },
  {
    key: "minimumRequirements",
    labelKey: "Minimum requirements",
    Body: MinimumRequirementsTabAdapter,
  },
  { key: "espd", labelKey: "Exclusion grounds", Body: EspdTabAdapter },
  { key: "evaluation", labelKey: "Evaluation", Body: EvaluationTabAdapter },
  { key: "clarification", labelKey: "Clarification", Body: ClarificationTab },
];

const DEFAULT_TAB: BidDetailTab = "overview";

const parseBidDetailTab = (value: string | null): BidDetailTab =>
  TABS.find((tab) => tab.key === value)?.key ?? DEFAULT_TAB;

const formatPrice = (price: BuyerPublicBidDetailDto["bid"]["price"]): string =>
  price?.value != null
    ? formatEstimatedValue(price.value, price.currency)
    : DASH;

const formatEspdHeader = (
  espd: BuyerPublicBidEspdDto | undefined,
  t: (value: string, options?: { count: number }) => string,
) => {
  if (!espd?.hasResponse)
    return { value: DASH, tone: "default" as const, pending: true };
  if (espd.deviationCount > 0) {
    return {
      value: t("espd_deviation_count", { count: espd.deviationCount }),
      tone: "issue" as const,
      pending: false,
    };
  }
  if (espd.unansweredCount > 0) {
    return {
      value: t("espd_unanswered_count", { count: espd.unansweredCount }),
      tone: "neutral" as const,
      pending: false,
    };
  }
  return { value: t("OK"), tone: "ok" as const, pending: false };
};

export const BidDetailView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDateTime = useDisplayDateWithTime();
  const { eventId, bidId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = useSub();

  if (!sub.hasSourcingEventV2Access) {
    return (
      <Center py="20">
        <Text color="smMuted">
          {t("Your organization does not have access to this feature.")}
        </Text>
      </Center>
    );
  }

  if (!eventId || !bidId) {
    return (
      <NotFound
        title={t("This link is not valid.")}
        backUrl={urls.v2.sourcingEvents.viewAll}
        backLabel={t("Back to procurements")}
        onNavigate={navigate}
      />
    );
  }

  return (
    <BidDetailPage
      eventId={eventId}
      bidId={bidId}
      activeTab={parseBidDetailTab(searchParams.get("tab"))}
      onTabChange={(tab) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", tab);
        setSearchParams(next, { replace: true });
      }}
      navigate={navigate}
      displayDateTime={displayDateTime}
    />
  );
};

type Navigate = ReturnType<typeof useNavigate>;

const BidDetailPage = ({
  eventId,
  bidId,
  activeTab,
  onTabChange,
  navigate,
  displayDateTime,
}: {
  eventId: string;
  bidId: string;
  activeTab: BidDetailTab;
  onTabChange: (tab: BidDetailTab) => void;
  navigate: Navigate;
  displayDateTime: (value: string) => string;
}) => {
  const { t } = useTranslation();
  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useGetPublicBidForBuyerQuery({ eventId, bidId });
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const twoStage = isTwoStageProcedure(event?.publicContext?.procedureType);
  const evaluation = useGetEvaluationQuery({ eventId });
  const awarded = isAwardDecided(evaluation.data?.bids);
  const bidsTabUrl = `${urls.v2.sourcingEvents.view.go(eventId)}?tab=bids`;

  const summary = useMemo(
    () =>
      deriveBidEvaluationSummary(evaluation.data, detail?.bid.id ?? "", {
        isLoading: evaluation.isLoading,
        isError: evaluation.isError,
      }),
    [evaluation.data, evaluation.isLoading, evaluation.isError, detail?.bid.id],
  );

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  if (isError || !detail) {
    const notFound =
      isError && error != null && "status" in error && error.status === 404;
    return (
      <NotFound
        title={
          notFound
            ? t("The bid could not be found.")
            : t("Could not load the bid.")
        }
        backUrl={bidsTabUrl}
        backLabel={t("Back to bids")}
        onNavigate={navigate}
      />
    );
  }

  const { bid } = detail;
  const supplierName = detail.ownerName || t("Unknown");
  const visibleTabs = TABS.filter(
    (tab) => !(twoStage && (tab.key === "qualifications" || tab.key === "espd")),
  );
  const resolvedActiveTab =
    visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : DEFAULT_TAB;
  const activeIndex = Math.max(
    0,
    visibleTabs.findIndex((tab) => tab.key === resolvedActiveTab),
  );
  const espdHeader = formatEspdHeader(detail.espd, t);

  return (
    <Box>
      <Grid
        templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) 340px" }}
        gap="6"
        alignItems="start"
      >
        <GridItem
          minW="0"
          gridColumn={{ base: "1", lg: "1" }}
          gridRow={{ base: "1", lg: "1" }}
        >
          <Box
            bg="smBackground"
            rounded="xl"
            border="1px solid"
            borderColor="smBorder"
            p={{ base: "4", md: "6" }}
          >
            <Breadcrumb
              separator={
                <Icon as={FiChevronRight} color="smMuted" boxSize="3" />
              }
              fontSize="sm"
              mb="3"
              sx={{
                ol: {
                  flexWrap: "wrap",
                  rowGap: 1,
                },
              }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={urls.v2.sourcingEvents.viewAll}
                  color="smPrimary"
                >
                  {t("All procurements")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={urls.v2.sourcingEvents.view.go(eventId)}
                  color="smPrimary"
                  isTruncated
                  maxW={{ base: "180px", md: "280px" }}
                >
                  {detail.eventTitle || t("Untitled")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={bidsTabUrl}
                  color="smPrimary"
                >
                  {t("Bids")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  color="smMuted"
                  isTruncated
                  maxW={{ base: "180px", md: "280px" }}
                >
                  {supplierName}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <HStack spacing="3" align="center" flexWrap="wrap" mb="1">
              <Heading as="h1" size="lg" color="smText">
                {supplierName}
              </Heading>
              <PublicBidStatusTag
                status={bid.status}
                awarded={awarded}
                size="md"
              />
            </HStack>
            {detail.ownerOrganizationNumber && (
              <Text fontSize="sm" color="smMuted" fontFamily="mono" mb="5">
                {`${t("Organization number")}: ${detail.ownerOrganizationNumber}`}
              </Text>
            )}

            <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing="3">
              <HeaderStat
                icon={FiTag}
                label={t("Total price")}
                value={formatPrice(bid.price)}
                tone="primary"
                mono
              />
              <HeaderStat
                icon={FiCalendar}
                label={t("Submitted")}
                value={
                  bid.submittedAt ? displayDateTime(bid.submittedAt) : DASH
                }
              />
              <HeaderStat
                icon={FiBarChart2}
                label={t("Ranking")}
                value={formatRankLabel(summary, t)}
                mono
                pending={summary.state === "loading"}
              />
              <HeaderStat
                icon={FiShield}
                label={t("Exclusion grounds")}
                value={espdHeader.value}
                tone={espdHeader.tone}
                pending={espdHeader.pending}
                onClick={
                  twoStage || resolvedActiveTab === "espd"
                    ? undefined
                    : () => onTabChange("espd")
                }
              />
            </SimpleGrid>
          </Box>
        </GridItem>

        <GridItem
          gridColumn={{ base: "1", lg: "2" }}
          gridRow={{ base: "2", lg: "1" }}
          h={{ lg: "0" }}
          overflow={{ lg: "visible" }}
        >
          <Box position={{ lg: "sticky" }} top={{ lg: "0" }}>
            <BidDetailSidebar
              detail={detail}
              summary={summary}
              awarded={awarded}
              onMarkWinner={() =>
                navigate(
                  `${urls.v2.sourcingEvents.view.go(eventId)}?tab=award&bid=${detail.bid.id}`,
                )
              }
              onOpenRejectionDetails={() => {
                onTabChange("overview");
                window.requestAnimationFrame(() => {
                  document
                    .getElementById("bid-rejection-details")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
            />
          </Box>
        </GridItem>

        <GridItem
          minW="0"
          gridColumn={{ base: "1", lg: "1" }}
          gridRow={{ base: "3", lg: "2" }}
        >
          <Tabs
            index={activeIndex}
            onChange={(i) => {
              const next = visibleTabs[i].key;
              if (next !== resolvedActiveTab) onTabChange(next);
            }}
            colorScheme="teal"
            isLazy
            lazyBehavior="keepMounted"
          >
            <TabList overflowX="auto" overflowY="hidden">
              {visibleTabs.map((tab) => (
                <Tab key={tab.key} whiteSpace="nowrap">
                  {t(tab.labelKey)}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {visibleTabs.map((tab) => (
                <TabPanel key={tab.key} px="0">
                  <tab.Body detail={detail} />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
    </Box>
  );
};

const NotFound = ({
  title,
  backUrl,
  backLabel,
  onNavigate,
}: {
  title: string;
  backUrl: string;
  backLabel: string;
  onNavigate: Navigate;
}) => (
  <Center py="20">
    <Flex direction="column" align="center" gap="4">
      <Text color="smMuted">{title}</Text>
      <Button
        variant="outline"
        size="sm"
        leftIcon={<Icon as={FiArrowLeft} />}
        onClick={() => onNavigate(backUrl)}
      >
        {backLabel}
      </Button>
    </Flex>
  </Center>
);
